import {
  PAYROLL_ROSTER_FIELD_TYPE,
  type PayrollRosterContract,
  type PayrollRosterDraftMember,
  type PayrollRosterField,
  type PayrollRosterMember,
  type PayrollRosterFormValues,
  type PayrollRosterValue,
} from "@/payroll/roster/types";
import { contractFieldOptions } from "./contract";

/** Prepare a separate form value object without changing the API member. */
export const toPayrollRosterFormValues = (
  member: PayrollRosterMember,
): PayrollRosterFormValues => ({
  ...member,
  field_values: Object.fromEntries(
    Object.entries(member.field_values).map(([key, value]) => [
      key,
      typeof value === "number" ? String(value) : value,
    ]),
  ),
});

// Shared by form validation and serialization so invalid values cannot become
// zero or JSON null through implicit conversion.
const parseRosterNumber = (
  value: PayrollRosterValue | undefined,
): number | null => {
  if (value == null || (typeof value === "string" && value.trim() === ""))
    return null;
  if (
    (typeof value !== "string" && typeof value !== "number") ||
    !Number.isFinite(Number(value))
  ) {
    throw new Error("Enter a valid number");
  }
  return Number(value);
};

/** Restore declared numeric types before the form reaches the save callback. */
export const toPayrollRosterMember = (
  values: PayrollRosterFormValues,
  fields: PayrollRosterField[],
): PayrollRosterMember => {
  const numberKeys = new Set(
    fields
      .filter((field) => field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number)
      .map((field) => field.key),
  );
  return {
    ...values,
    field_values: Object.fromEntries(
      Object.entries(values.field_values).map(([key, value]) => [
        key,
        numberKeys.has(key) ? parseRosterNumber(value) : value,
      ]),
    ),
  };
};

export const createPayrollRosterMember = (
  fields: PayrollRosterField[],
): PayrollRosterMember => ({
  service_titan_employee_id: "",
  display_name: "",
  field_values: Object.fromEntries(
    fields
      .filter((field) => field.default !== undefined)
      .map((field) => [field.key, field.default ?? null]),
  ),
});

export const createPayrollRosterDraftMembers = (
  members: PayrollRosterMember[],
): PayrollRosterDraftMember[] =>
  members.map((member, index) => ({
    id: `existing-${index}`,
    isNew: false,
    member: { ...member },
  }));

export const isBlankRosterValue = (value: PayrollRosterValue | undefined) =>
  value === undefined || value === null || value === "";

export interface PayrollRosterMemberErrors {
  display_name?: string;
  service_titan_employee_id?: string;
  field_values: Record<string, string>;
}

export const validatePayrollRosterMember = (
  member: PayrollRosterMember,
  fields: PayrollRosterField[],
  contract: PayrollRosterContract = {},
): PayrollRosterMemberErrors => {
  const errors: PayrollRosterMemberErrors = { field_values: {} };

  if (!member.display_name.trim()) {
    errors.display_name = "Name is required";
  }

  if (!member.service_titan_employee_id.trim()) {
    errors.service_titan_employee_id = "ServiceTitan employee ID is required";
  }

  fields.forEach((field) => {
    const value = member.field_values[field.key];

    if (field.required && isBlankRosterValue(value)) {
      errors.field_values[field.key] = `${field.label} is required`;
      return;
    }

    if (field.field_type === PAYROLL_ROSTER_FIELD_TYPE.number) {
      try {
        parseRosterNumber(value);
      } catch {
        errors.field_values[field.key] =
          `Enter a valid number for ${field.label}`;
      }
    }

    if (
      field.field_type === PAYROLL_ROSTER_FIELD_TYPE.fixedOption &&
      !isBlankRosterValue(value) &&
      !contractFieldOptions(field.key, contract, field.options).includes(
        String(value),
      )
    ) {
      errors.field_values[field.key] = `Choose a valid ${field.label}`;
    }
  });

  return errors;
};

export const hasPayrollRosterMemberErrors = (
  errors: PayrollRosterMemberErrors,
) =>
  !!errors.display_name ||
  !!errors.service_titan_employee_id ||
  Object.keys(errors.field_values).length > 0;

// The members whose saved values no longer satisfy the contract — a fixed_option
// value that was removed/renamed out from under them, a newly-required field
// left blank, or a type change that left a value invalid. Reuses the exact
// validation pass that powers the Review/Publish gate, so this count always
// matches the reason publish is blocked. Returns the members themselves so the
// banner can show a count today and deep-link to the affected rows later.
export const membersNeedingReassignment = (
  members: PayrollRosterMember[],
  fields: PayrollRosterField[],
  contract: PayrollRosterContract,
): PayrollRosterMember[] =>
  members.filter((member) =>
    hasPayrollRosterMemberErrors(
      validatePayrollRosterMember(member, fields, contract),
    ),
  );
